import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import savedCampaignService from '../../services/savedCampaignService';
import analyticsService from '../../services/analyticsService';
import { isCampaignActive } from '../../services/campaignService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Icons
import {
  Bookmark,
  Star,
  Clock,
  Users,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Camera,
  Video,
  Globe,
  Smartphone,
  CheckCircle2,
  Sparkles,
  LayoutGrid,
  List,
  Compass,
} from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

// --- Helpers (same as Campaigns.js) ---

const formatDate = (timestamp) => {
  if (!timestamp) return 'TBD';
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'TBD';
  }
};

const formatPeso = (amount) => {
  const cleaned = (amount || 0).toString().replace(/[₱,\s]/g, '');
  const num = parseInt(cleaned, 10);
  if (isNaN(num)) return '₱0';
  return `₱${num.toLocaleString('en-US')}`;
};

const getCompensationLabel = (campaign) => {
  if (campaign.compensationType === 'monetary') {
    return formatPeso(campaign.compensationAmount || campaign.budget);
  }
  if (campaign.compensationType === 'product') {
    return campaign.productDetails || 'Product';
  }
  return `${formatPeso(campaign.compensationAmount || campaign.budget)} + Product`;
};

const getDaysUntil = (timestamp) => {
  if (!timestamp) return null;
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const getPlatformIcon = (platform) => {
  const p = platform?.toLowerCase();
  if (p === 'instagram') return Camera;
  if (p === 'youtube') return Video;
  if (p === 'tiktok') return Smartphone;
  if (p === 'twitch') return Video;
  if (p === 'facebook') return Globe;
  if (p === 'twitter' || p === 'x') return Globe;
  return Globe;
};

// --- Component ---

const SavedCampaigns = () => {
  const { currentUser } = useAuth();
  const { showError } = useFeedback();
  const [campaigns, setCampaigns] = useState([]);
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const itemsPerPage = 9;

  const [brandData, setBrandData] = useState({});

  // --- Data fetching ---

  const fetchSavedCampaigns = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Get saved campaign IDs
      const savedIds = await savedCampaignService.getSavedCampaignIds(currentUser.uid);

      if (savedIds.length === 0) {
        setCampaigns([]);
        setSavedCampaigns([]);
        return;
      }

      setSavedCampaigns(savedIds);

      // Fetch full campaign data for each saved ID
      const campaignPromises = savedIds.map(async (campaignId) => {
        try {
          const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
          if (campaignDoc.exists()) {
            return { id: campaignDoc.id, ...campaignDoc.data() };
          }
          return null;
        } catch {
          return null;
        }
      });

      const results = await Promise.all(campaignPromises);
      const validCampaigns = results.filter(Boolean);

      setCampaigns(validCampaigns);

      // Fetch brand data in parallel
      const brandIds = [...new Set(validCampaigns.map((c) => c.brandId).filter(Boolean))];
      if (brandIds.length > 0) {
        const brandPromises = brandIds.map(async (brandId) => {
          try {
            const brandDoc = await getDoc(doc(db, 'users', brandId));
            if (brandDoc.exists()) {
              return [brandId, brandDoc.data()];
            }
          } catch {
            // skip
          }
          return [brandId, null];
        });

        const brandResults = await Promise.all(brandPromises);
        const brandMap = {};
        brandResults.forEach(([id, data]) => {
          if (data) brandMap[id] = data;
        });
        setBrandData(brandMap);
      }
    } catch (err) {
      console.error('Error fetching saved campaigns:', err);
      setError('Failed to load saved campaigns. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSavedCampaigns();
  }, [fetchSavedCampaigns]);

  // --- Actions ---

  const toggleSaveCampaign = async (campaignId) => {
    if (!currentUser || savingId === campaignId) return;

    const wasSaved = savedCampaigns.includes(campaignId);

    // Optimistic update
    setSavedCampaigns((prev) =>
      wasSaved ? prev.filter((id) => id !== campaignId) : [...prev, campaignId]
    );
    setSavingId(campaignId);

    try {
      await savedCampaignService.toggleSavedCampaign(currentUser.uid, campaignId, wasSaved);

      // If unsaving, remove from the campaigns list too
      if (wasSaved) {
        setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
      }

      analyticsService.trackUserEvent(currentUser.uid, wasSaved ? 'unsave_campaign' : 'save_campaign', {
        campaignId,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      // Rollback
      setSavedCampaigns((prev) =>
        wasSaved ? [...prev, campaignId] : prev.filter((id) => id !== campaignId)
      );
      showError(
        'Could not update saved campaign',
        'Something went wrong. Please try again.',
        3000
      );
    } finally {
      setSavingId(null);
    }
  };

  // --- Derived data ---

  const filteredAndSortedCampaigns = useMemo(() => {
    let result = campaigns.filter((campaign) => {
      const brand = brandData[campaign.brandId] || { name: 'Unknown Brand' };
      const matchesSearch =
        !searchTerm ||
        campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'deadline':
          return (getDaysUntil(a.applicationDeadline || a.endDate) ?? Infinity) - (getDaysUntil(b.applicationDeadline || b.endDate) ?? Infinity);
        case 'compensation':
          return (parseInt((b.compensationAmount || b.budget || '0').toString().replace(/\D/g, ''), 10) || 0) - (parseInt((a.compensationAmount || a.budget || '0').toString().replace(/\D/g, ''), 10) || 0);
        case 'spots':
          return (Math.max(0, (a.totalSpots || 0) - (a.filledSpots || 0))) - (Math.max(0, (b.totalSpots || 0) - (b.filledSpots || 0)));
        case 'newest':
        default:
          return 0;
      }
    });

    return result;
  }, [campaigns, brandData, searchTerm, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedCampaigns.length / itemsPerPage);
  const paginatedCampaigns = filteredAndSortedCampaigns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when search/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  // --- Constants ---

  const sortOptions = [
    { value: 'newest', label: 'Recently Saved' },
    { value: 'deadline', label: 'Deadline Soon' },
    { value: 'compensation', label: 'Highest Compensation' },
    { value: 'spots', label: 'Most Spots Left' },
  ];

  // --- Render ---

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Saved Campaigns</h2>
        <p className="text-gray-600">Campaigns you've bookmarked to revisit later</p>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchSavedCampaigns}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </div>
      )}

      {/* Results */}
      {!loading && !error && (
        <>
          {/* Search + Sort + View toggle */}
          {campaigns.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="relative flex-grow">
                    <input
                      type="text"
                      className="block w-full px-4 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Search saved campaigns"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Sort dropdown */}
                  <div className="sm:w-48">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* View toggle */}
                  <div className="flex border border-gray-300 rounded-md overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                      aria-label="Grid view"
                      title="Grid view"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                      aria-label="List view"
                      title="List view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results count */}
          {campaigns.length > 0 && (
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900">{paginatedCampaigns.length}</span> of{' '}
                <span className="font-medium text-gray-900">{filteredAndSortedCampaigns.length}</span> saved campaign{filteredAndSortedCampaigns.length === 1 ? '' : 's'}
              </p>
            </div>
          )}

          {/* Campaign Cards */}
          {paginatedCampaigns.length > 0 ? (
            viewMode === 'grid' ? (
              /* ---- GRID VIEW ---- */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedCampaigns.map((campaign) => {
                  const spotsLeft = Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0));
                  const daysLeft = getDaysUntil(campaign.applicationDeadline || campaign.endDate);
                  const platforms = campaign.requiredPlatforms || campaign.preferredPlatforms || [];

                  return (
                    <div
                      key={campaign.id}
                      className="bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                    >
                      {/* Campaign Image or placeholder */}
                      <div className="w-full h-32 overflow-hidden rounded-t-lg relative bg-gradient-to-br from-primary/10 to-blue-400/10">
                        {campaign.imageUrl ? (
                          <img
                            src={campaign.imageUrl}
                            alt={campaign.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Sparkles className="h-8 w-8 text-primary/30" />
                          </div>
                        )}
                      </div>

                      <div className="p-4 flex-1 flex flex-col">
                        {/* Title + bookmark */}
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-base font-semibold text-gray-900 line-clamp-1 flex-1 min-w-0">{campaign.name}</h3>
                          <button
                            onClick={() => toggleSaveCampaign(campaign.id)}
                            disabled={savingId === campaign.id}
                            className="text-gray-400 hover:text-primary ml-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Remove from saved"
                            title="Remove from saved"
                          >
                            {savingId === campaign.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Bookmark className="h-4 w-4 text-primary" fill="currentColor" />
                            )}
                          </button>
                        </div>

                        {/* Brand */}
                        <div className="flex items-center text-sm text-gray-500 mb-2">
                          {brandData[campaign.brandId] && (
                            <>
                              <img
                                src={brandData[campaign.brandId].logo || brandData[campaign.brandId].profileImageUrl}
                                alt={brandData[campaign.brandId].name || brandData[campaign.brandId].displayName}
                                className="w-4 h-4 rounded-full mr-1.5 object-cover"
                                onError={(e) => { e.target.src = '/assets/default-logo.png'; }}
                              />
                              <span className="truncate">{brandData[campaign.brandId].name || brandData[campaign.brandId].displayName || 'Unknown Brand'}</span>
                            </>
                          )}
                        </div>

                        {/* Category + status badges */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {campaign.category}
                          </span>
                          {isCampaignActive(campaign) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Ended
                            </span>
                          )}
                          {platforms.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                              {platforms.slice(0, 3).map((platform, idx) => {
                                const Icon = getPlatformIcon(platform);
                                return <Icon key={idx} className="h-3 w-3" />;
                              })}
                            </span>
                          )}
                        </div>

                        {/* Urgency indicator */}
                        {(daysLeft !== null && daysLeft <= 3 && daysLeft >= 0) && (
                          <div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
                            <Clock className="h-3.5 w-3.5" />
                            {daysLeft === 0 ? 'Deadline is today!' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to apply`}
                          </div>
                        )}
                        {spotsLeft > 0 && spotsLeft <= 3 && (
                          <div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-md">
                            <Users className="h-3.5 w-3.5" />
                            Only {spotsLeft} spot{spotsLeft === 1 ? '' : 's'} left!
                          </div>
                        )}

                        {/* Compact info row */}
                        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                          <div>
                            <span className="text-gray-500">Compensation:</span>{' '}
                            <span className="font-semibold text-gray-900">{getCompensationLabel(campaign)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-gray-500 text-xs block">Spots Left</span>
                            <span className="font-semibold text-gray-900">{spotsLeft}</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-gray-50 border-t border-gray-200">
                        <Link
                          to={`/influencer/campaigns/${campaign.id}`}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ---- LIST VIEW ---- */
              <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                {/* Table header */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-4">Campaign</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-2">Compensation</div>
                  <div className="col-span-2">Deadline</div>
                  <div className="col-span-1 text-center">Spots</div>
                  <div className="col-span-1 text-right">Action</div>
                </div>

                {paginatedCampaigns.map((campaign) => {
                  const spotsLeft = Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0));
                  const daysLeft = getDaysUntil(campaign.applicationDeadline || campaign.endDate);

                  return (
                    <div
                      key={campaign.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors items-center"
                    >
                      {/* Campaign name + brand */}
                      <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/10 to-blue-400/10">
                          {campaign.imageUrl ? (
                            <img src={campaign.imageUrl} alt={campaign.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Sparkles className="h-5 w-5 text-primary/30" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{campaign.name}</h3>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            {brandData[campaign.brandId] && (
                              <>
                                <img
                                  src={brandData[campaign.brandId].logo || brandData[campaign.brandId].profileImageUrl}
                                  alt={brandData[campaign.brandId].name || brandData[campaign.brandId].displayName}
                                  className="w-3.5 h-3.5 rounded-full mr-1 object-cover"
                                  onError={(e) => { e.target.src = '/assets/default-logo.png'; }}
                                />
                                <span className="truncate">{brandData[campaign.brandId].name || brandData[campaign.brandId].displayName || 'Unknown Brand'}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Bookmark (always saved — click to remove) */}
                        <button
                          onClick={() => toggleSaveCampaign(campaign.id)}
                          disabled={savingId === campaign.id}
                          className="text-primary hover:text-gray-400 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Remove from saved"
                          title="Remove from saved"
                        >
                          {savingId === campaign.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Bookmark className="h-4 w-4" fill="currentColor" />
                          )}
                        </button>
                      </div>

                      {/* Category */}
                      <div className="md:col-span-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {campaign.category}
                        </span>
                        {!isCampaignActive(campaign) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Ended
                          </span>
                        )}
                      </div>

                      {/* Compensation */}
                      <div className="md:col-span-2 text-sm font-semibold text-gray-900">
                        {getCompensationLabel(campaign)}
                      </div>

                      {/* Deadline */}
                      <div className="md:col-span-2 text-sm text-gray-600">
                        <div>{formatDate(campaign.applicationDeadline || campaign.endDate)}</div>
                        {(daysLeft !== null && daysLeft <= 3 && daysLeft >= 0) && (
                          <div className="text-xs font-medium text-orange-600 flex items-center gap-1 mt-0.5">
                            <Clock className="h-3 w-3" />
                            {daysLeft === 0 ? 'Today!' : `${daysLeft}d left`}
                          </div>
                        )}
                      </div>

                      {/* Spots */}
                      <div className="md:col-span-1 text-center">
                        <span className={`text-sm font-medium ${
                          spotsLeft > 0 && spotsLeft <= 3 ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {spotsLeft}
                        </span>
                      </div>

                      {/* Action */}
                      <div className="md:col-span-1 text-right">
                        <Link
                          to={`/influencer/campaigns/${campaign.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            /* ---- EMPTY STATE ---- */
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                {campaigns.length === 0 ? (
                  <Bookmark className="h-12 w-12 text-gray-300" />
                ) : (
                  <Bookmark className="h-12 w-12 text-gray-300" />
                )}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {campaigns.length === 0 ? 'No saved campaigns yet' : 'No campaigns found'}
              </h3>
              <p className="text-gray-500 mb-4">
                {campaigns.length === 0
                  ? 'Bookmark campaigns from the listings page to save them here for later.'
                  : 'No saved campaigns match your search. Try a different keyword.'}
              </p>
              {campaigns.length === 0 ? (
                <Link
                  to="/influencer/campaigns"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                >
                  <Compass className="h-4 w-4" />
                  Browse Campaigns
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Clear search
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-primary text-white border border-primary'
                      : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SavedCampaigns;
